import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { onSnapshot } from 'firebase/firestore';
import { getFirestore, doc } from 'firebase/firestore';
import { vacationBalanceService } from '../../services/vacationBalanceService';
import type { VacationBalance } from '../../models/vacation.types';

export const VACATION_BALANCE_QUERY_KEY = 'vacationBalance';

const POLL_INTERVAL_MS = 5000;
export const BALANCE_POLL_TIMEOUT_MS = 30000;

export interface UseVacationBalanceQueryOptions {
    poll?: boolean;
}

export function useVacationBalanceQuery(
    collaboratorId?: string,
    { poll = false }: UseVacationBalanceQueryOptions = {}
) {
    const pollStartedAtRef = useRef<number | null>(null);
    const [timedOut, setTimedOut] = useState(false);
    const queryClient = useQueryClient();
    const db = getFirestore();

    useEffect(() => {
        if (!collaboratorId) return;

        const unsubscribe = onSnapshot(
            doc(db, 'vacationBalances', collaboratorId),
            (snap) => {
                if (snap.exists()) {
                    const data = snap.data();
                    queryClient.setQueryData(
                        [VACATION_BALANCE_QUERY_KEY, collaboratorId],
                        data as VacationBalance
                    );
                }
            },
            () => {}
        );

        return () => unsubscribe();
    }, [collaboratorId, queryClient, db]);

    useEffect(() => {
        if (poll) {
            if (pollStartedAtRef.current === null) {
                pollStartedAtRef.current = Date.now();
                setTimedOut(false);
            }
        } else {
            pollStartedAtRef.current = null;
            setTimedOut(false);
        }
    }, [poll]);

    useEffect(() => {
        if (!poll || pollStartedAtRef.current === null) return;

        const timeout = setTimeout(() => {
            setTimedOut(true);
        }, BALANCE_POLL_TIMEOUT_MS);

        return () => clearTimeout(timeout);
    }, [poll]);

    const queryResult = useQuery({
        queryKey: [VACATION_BALANCE_QUERY_KEY, collaboratorId],
        queryFn: () => vacationBalanceService.getByCollaboratorId(collaboratorId as string),
        enabled: Boolean(collaboratorId),
        staleTime: 5 * 60 * 1000,
        retryOnMount: false,
        refetchInterval: () => {
            if (!poll || timedOut) return false;
            return POLL_INTERVAL_MS;
        },
    });

    const isPolling = poll && !timedOut;

    return {
        balance: queryResult.data ?? null,
        isLoading: queryResult.isLoading,
        error: queryResult.error,
        refetch: queryResult.refetch,
        isPolling,
    };
}
